import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { chatAPI } from '../services/api';
import io from 'socket.io-client';
import logger from '../utils/logger';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { isAuthenticated, user, token } = useAuth();
  const [socket, setSocket] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isTyping, setIsTyping] = useState({});
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef({});
  const activeChatRef = useRef(null); // Ref to always have latest activeChat value
  const fetchConversationsTimeoutRef = useRef(null); // Debounce for fetchConversations

  // Helper to get Socket.io server URL
  const getSocketBaseURL = () => {
    // Check for environment variable first (production) - this is the most reliable
    if (process.env.REACT_APP_SOCKET_URL) {
      logger.debug('Using Socket URL from environment:', process.env.REACT_APP_SOCKET_URL);
      return process.env.REACT_APP_SOCKET_URL;
    }
    
    const hostname = window.location.hostname;
    const protocol = window.location.protocol; // http: or https:
    
    // If accessing from localhost, use localhost for backend
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      logger.debug('Using localhost Socket URL');
      return 'http://localhost:5000';
    }
    
    // If accessing from Vercel (vercel.app domain), warn about missing env var
    if (hostname.includes('vercel.app')) {
      logger.error('❌ CRITICAL: REACT_APP_SOCKET_URL not set in Vercel!');
      logger.error('Please add it in Vercel Dashboard → Your Project → Settings → Environment Variables');
      logger.error('Value should be your backend Socket.io URL (e.g., https://your-app.railway.app)');
      // Return a URL that will fail gracefully
      return '/socket.io'; // This will fail, but at least the app won't crash immediately
    }
    
    // If accessing from production domain (batinosgarden.shop), use API subdomain
    // Always use HTTPS in production for security
    if (hostname === 'batinosgarden.shop' || hostname === 'www.batinosgarden.shop') {
      // Force HTTPS in production
      const prodProtocol = protocol === 'https:' ? 'https:' : 'https:';
      return `${prodProtocol}//api.batinosgarden.shop`;
    }
    
    // If accessing from network IP, use the same IP for backend
    // This allows laptop/phone access to work automatically
    return `http://${hostname}:5000`;
  };

  // Initialize Socket.io connection
  useEffect(() => {
    // Get token from localStorage if not provided from context
    const authToken = token || localStorage.getItem('accessToken');
    
    if (!isAuthenticated || !authToken) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
      return;
    }

    const socketURL = getSocketBaseURL();
    logger.debug('🔌 Connecting to Socket.io at:', socketURL);
    logger.debug('🔑 Token available:', !!authToken);
    
    const newSocket = io(socketURL, {
      auth: {
        token: authToken
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity, // Keep trying to reconnect indefinitely
      timeout: 20000,
      // Force upgrade to websocket for better performance
      upgrade: true,
      // Better error handling
      forceNew: false,
      // Enable multiplexing
      multiplex: true
    });

    newSocket.on('connect', () => {
      logger.info('✅ Socket.io connected');
      setError(null);
      
      // Automatically join active chat room if one exists
      if (activeChat) {
        newSocket.emit('join-chat', activeChat._id);
      }
    });

    newSocket.on('disconnect', () => {
      logger.info('❌ Socket.io disconnected');
    });

    newSocket.on('connect_error', (err) => {
      logger.error('Socket.io connection error:', err);
      logger.error('Connection URL:', socketURL);
      logger.error('Error details:', err.message);
      
      // Provide more helpful error messages
      let errorMessage = `Failed to connect to chat server: ${err.message}`;
      if (err.message.includes('CORS')) {
        errorMessage = 'Chat server connection blocked. Please check your network settings.';
      } else if (err.message.includes('timeout')) {
        errorMessage = 'Chat server connection timeout. Please check your internet connection.';
      } else if (err.message.includes('Authentication')) {
        errorMessage = 'Authentication failed. Please try logging out and back in.';
      }
      
      setError(errorMessage);
    });
    
    // Handle reconnection events
    newSocket.on('reconnect', (attemptNumber) => {
      logger.info(`✅ Socket.io reconnected after ${attemptNumber} attempts`);
      setError(null);
      
      // Automatically rejoin active chat room after reconnection
      // Use ref to get latest activeChat value (avoids stale closure)
      const currentActiveChat = activeChatRef.current;
      if (currentActiveChat) {
        const chatId = currentActiveChat._id?.toString() || currentActiveChat._id;
        newSocket.emit('join-chat', chatId);
        logger.debug('📥 Re-joined chat room after reconnection:', chatId);
      }
    });
    
    newSocket.on('reconnect_attempt', (attemptNumber) => {
      logger.debug(`🔄 Socket.io reconnection attempt ${attemptNumber}`);
    });
    
    newSocket.on('reconnect_error', (err) => {
      logger.error('Socket.io reconnection error:', err);
    });
    
    newSocket.on('reconnect_failed', () => {
      logger.error('❌ Socket.io reconnection failed after all attempts');
      setError('Unable to reconnect to chat server. Please refresh the page.');
    });

    newSocket.on('new-message', (data) => {
      if (data.message) {
        // Use ref to get latest activeChat value (avoids stale closure)
        const currentActiveChat = activeChatRef.current;
        
        // Normalize chat IDs for comparison - handle both ObjectId and string formats
        const messageChatId = data.message.chat?.toString() || 
                              data.message.chat || 
                              (typeof data.message.chat === 'object' && data.message.chat?._id ? data.message.chat._id.toString() : null) ||
                              String(data.message.chat || '');
        const activeChatId = currentActiveChat?._id?.toString() || 
                            currentActiveChat?._id || 
                            (currentActiveChat?._id ? String(currentActiveChat._id) : null);
        
        // If this message is for the active chat, add it to messages
        if (activeChatId && messageChatId && messageChatId === activeChatId) {
          logger.debug('✅ Adding message to active chat:', {
            messageChatId,
            activeChatId,
            messageId: data.message._id,
            sender: data.message.sender?.email
          });
          
          // Check if message is from another user (not the current user)
          const messageSenderId = data.message.sender?._id?.toString() || data.message.sender?._id;
          const currentUserId = user?._id?.toString() || user?._id;
          const isFromOtherUser = messageSenderId && currentUserId && messageSenderId !== currentUserId;
          
          // If message is from another user and chat is open, mark as read immediately
          if (isFromOtherUser && socket) {
            // Mark messages as read when viewing active chat
            socket.emit('mark-read', activeChatId);
            // Also call API to mark as read
            chatAPI.markAsRead(activeChatId).catch(err => {
              logger.error('Error marking messages as read:', err);
            });
          }
          
          setMessages(prev => {
            // Check if message already exists (from optimistic update)
            const existingIndex = prev.findIndex(msg => {
              const msgId = msg._id?.toString() || msg._id;
              const msgTempId = msg.tempId;
              const dataMsgId = data.message._id?.toString() || data.message._id;
              const dataTempId = data.message.tempId;
              
              return msgId === dataMsgId || 
                     (msgTempId && msgTempId === dataTempId) ||
                     (msg.isOptimistic && msg.content === data.message.content && 
                      Math.abs(new Date(msg.createdAt) - new Date(data.message.createdAt)) < 5000);
            });
            
            if (existingIndex >= 0) {
              // Replace temporary message with real one
              const newMessages = [...prev];
              newMessages[existingIndex] = data.message;
              // Sort to ensure proper order
              return newMessages.sort((a, b) => {
                const dateA = new Date(a.createdAt);
                const dateB = new Date(b.createdAt);
                return dateA - dateB;
              });
            } else {
              // Add new message (from other user)
              const newMessages = [...prev, data.message];
              // Sort to ensure proper order
              return newMessages.sort((a, b) => {
                const dateA = new Date(a.createdAt);
                const dateB = new Date(b.createdAt);
                return dateA - dateB;
              });
            }
          });
        } else {
          logger.debug('⚠️ Message not for active chat:', {
            messageChatId,
            activeChatId,
            messageId: data.message._id
          });
        }
        
        // Always update conversation list when a new message arrives
        if (data.chat) {
          // Update conversation with new message data (includes unread count)
          updateConversation(data.chat);
          
          // Always refresh conversations to ensure unread counts are accurate
          // This ensures the badge updates in real-time even for active chat
          // Debounce to batch multiple rapid updates (e.g., auto-reply + user message)
          if (fetchConversationsTimeoutRef.current) {
            clearTimeout(fetchConversationsTimeoutRef.current);
          }
          fetchConversationsTimeoutRef.current = setTimeout(() => {
            fetchConversations();
            fetchConversationsTimeoutRef.current = null;
          }, 200);
        }
      }
    });

    newSocket.on('user-typing', (data) => {
      if (data.chatId === activeChat?._id) {
        setIsTyping(prev => ({
          ...prev,
          [data.userId]: data.isTyping
        }));

        // Clear typing indicator after 3 seconds
        if (typingTimeoutRef.current[data.userId]) {
          clearTimeout(typingTimeoutRef.current[data.userId]);
        }
        typingTimeoutRef.current[data.userId] = setTimeout(() => {
          setIsTyping(prev => ({
            ...prev,
            [data.userId]: false
          }));
        }, 3000);
      }
    });

    newSocket.on('messages-read', (data) => {
      const currentActiveChat = activeChatRef.current;
      if (data.chatId === currentActiveChat?._id) {
        setMessages(prev => prev.map(msg => 
          msg.sender._id !== data.userId ? { ...msg, read: true } : msg
        ));
      }
    });

    newSocket.on('error', (error) => {
      logger.error('Socket.io error:', error);
      setError(error.message || 'Chat error occurred');
      
      // If error is about sending a message, remove the last optimistic message
      if (error.message && error.message.includes('send')) {
        setMessages(prev => {
          // Remove the last optimistic message
          const lastMessage = prev[prev.length - 1];
          if (lastMessage && lastMessage.isOptimistic) {
            return prev.slice(0, -1);
          }
          return prev;
        });
      }
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      Object.values(typingTimeoutRef.current).forEach(timeout => clearTimeout(timeout));
      if (fetchConversationsTimeoutRef.current) {
        clearTimeout(fetchConversationsTimeoutRef.current);
        fetchConversationsTimeoutRef.current = null;
      }
    };
  }, [isAuthenticated, token]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      const response = await chatAPI.getConversations();
      if (response.success) {
        setConversations(response.data || []);
      }
    } catch (err) {
      logger.error('Error fetching conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Update conversation in list
  const updateConversation = useCallback((chatData) => {
    setConversations(prev => {
      const index = prev.findIndex(c => {
        const cId = c._id?.toString() || c._id;
        const chatId = chatData._id?.toString() || chatData._id;
        return cId === chatId;
      });
      
      if (index >= 0) {
        const updated = [...prev];
        const currentConversation = updated[index];
        
        // Extract unread count for current user from socket payload
        // Socket sends unreadCount as object: { "userId1": 5, "userId2": 3 }
        // But we need just the number for the current user
        let unreadCount = currentConversation.unreadCount || 0;
        if (chatData.unreadCount) {
          if (typeof chatData.unreadCount === 'object' && !Array.isArray(chatData.unreadCount)) {
            // It's an object with user IDs as keys, extract current user's count
            const userId = user?._id?.toString() || user?._id;
            if (userId) {
              unreadCount = chatData.unreadCount[userId] || 
                           chatData.unreadCount[userId.toString()] || 
                           0;
            }
          } else if (typeof chatData.unreadCount === 'number') {
            // It's already a number (from API response)
            unreadCount = chatData.unreadCount;
          }
        }
        
        // Update conversation with proper unread count
        updated[index] = { 
          ...currentConversation, 
          ...chatData,
          unreadCount // Override with extracted number
        };
        
        return updated.sort((a, b) => {
          const dateA = new Date(a.lastMessageAt || a.createdAt || 0);
          const dateB = new Date(b.lastMessageAt || b.createdAt || 0);
          return dateB - dateA;
        });
      }
      return prev;
    });
  }, [user]);

  // Keep activeChatRef in sync with activeChat state
  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  // Load conversations on mount and when authenticated
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Create or get chat
  const createOrGetChat = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await chatAPI.createChat();
      if (response.data && response.data.success) {
        await fetchConversations();
        return response.data.data;
      } else if (response.data) {
        // Response might be in response.data
        await fetchConversations();
        return response.data.data || response.data;
      }
      return null;
    } catch (err) {
      logger.error('Error creating chat:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create chat';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchConversations]);

  // Select a chat
  const selectChat = useCallback(async (chat) => {
    if (!socket) {
      setError('Not connected to chat server');
      return;
    }

    const chatId = chat._id?.toString() || chat._id;
    setActiveChat(chat);
    setMessages([]);
    setIsTyping({});

    // Join chat room (CRITICAL for receiving messages)
    socket.emit('join-chat', chatId);
    logger.debug('📥 Joined chat room:', chatId);

    // Fetch messages - fetch more messages to ensure we get all recent ones
    try {
      setLoading(true);
      // Fetch with a higher limit to ensure we get all messages including the latest
      const response = await chatAPI.getMessages(chatId, 1, 100);
      if (response.data && response.data.success) {
        const fetchedMessages = response.data.data?.messages || response.data.messages || [];
        // Sort messages by createdAt to ensure proper order
        const sortedMessages = fetchedMessages.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateA - dateB;
        });
        setMessages(sortedMessages);
        
        // Mark as read
        await chatAPI.markAsRead(chatId);
        socket.emit('mark-read', chatId);
        // Optimistically clear unread counts for this chat locally
        setConversations(prev =>
          prev.map(conv =>
            (conv._id?.toString() || conv._id) === chatId
              ? { ...conv, unreadCount: 0 }
              : conv
          )
        );
      } else if (response.success) {
        const fetchedMessages = response.data?.messages || [];
        // Sort messages by createdAt to ensure proper order
        const sortedMessages = fetchedMessages.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateA - dateB;
        });
        setMessages(sortedMessages);
        
        // Mark as read
        await chatAPI.markAsRead(chatId);
        socket.emit('mark-read', chatId);
        // Optimistically clear unread counts for this chat locally
        setConversations(prev =>
          prev.map(conv =>
            (conv._id?.toString() || conv._id) === chatId
              ? { ...conv, unreadCount: 0 }
              : conv
          )
        );
      }
    } catch (err) {
      logger.error('Error fetching messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [socket]);

  // Send a message
  const sendMessage = useCallback(async (content) => {
    if (!socket || !activeChat || !content.trim()) {
      return;
    }

    const trimmedContent = content.trim();
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    
    // Create optimistic message (appears instantly)
    const optimisticMessage = {
      _id: tempId,
      tempId: tempId,
      chat: activeChat._id,
      sender: {
        _id: user._id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role
      },
      content: trimmedContent,
      read: false,
      createdAt: new Date().toISOString(),
      isOptimistic: true
    };

    // Add optimistic message immediately
    setMessages(prev => [...prev, optimisticMessage]);

    // Send message to server
    const messageData = {
      chatId: activeChat._id,
      content: trimmedContent,
      tempId: tempId // Include tempId so server can match it
    };

    socket.emit('send-message', messageData);
  }, [socket, activeChat, user]);

  // Send typing indicator
  const sendTyping = useCallback((isTyping) => {
    if (!socket || !activeChat) return;
    
    socket.emit('typing', {
      chatId: activeChat._id,
      isTyping
    });
  }, [socket, activeChat]);

  // Get unread count for all conversations
  const getTotalUnreadCount = useCallback(() => {
    return conversations.reduce((total, conv) => total + (conv.unreadCount || 0), 0);
  }, [conversations]);

  const value = {
    socket,
    conversations,
    activeChat,
    messages,
    loading,
    error,
    isTyping,
    createOrGetChat,
    selectChat,
    sendMessage,
    sendTyping,
    fetchConversations,
    getTotalUnreadCount,
    setError
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

